import { getAccountCityStats, getStationCityStats } from "@/api/analytics.api"

// ---------------------------------------------------------------------------
// Helpers (private)
// ---------------------------------------------------------------------------

/**
 * Apply a single API result into the store via the generic `setDatasetStat`
 * mutation, keyed by dataset ("primary" or "comparison").
 */
function applyResult(vuex, stats, dataset = "primary") {
  if (Object.hasOwn(stats, "country_stats")) {
    vuex.commit("setDatasetStat", { dataset, key: "regional", value: stats.country_stats })
  }
  if (Object.hasOwn(stats, "device_stats")) {
    vuex.commit("setDatasetStat", { dataset, key: "device", value: stats.device_stats })
  }
  if (Object.hasOwn(stats, "period_stats")) {
    vuex.commit("setDatasetStat", { dataset, key: "period", value: stats.period_stats })
  } else if (Object.hasOwn(stats, "hour_stats")) {
    vuex.commit("setDatasetStat", { dataset, key: "period", value: stats.hour_stats })
  }
  if (Object.hasOwn(stats, "royalty_stats")) {
    vuex.commit("setDatasetStat", { dataset, key: "royalty", value: stats.royalty_stats })
  }
}

// ---------------------------------------------------------------------------
// Streaming request-state orchestrators
// ---------------------------------------------------------------------------

/**
 * Fetch a single set of requests with progressive loading indicators.
 * Used for primary-only fetches (no comparison).
 */
async function fetchWithRequestStates(vuex, requests, dataset) {
  requests.forEach(({ key }) => {
    vuex.commit("setStreamingRequestState", { key, loading: true, error: null })
  })

  const results = await Promise.allSettled(
    requests.map(({ key, promise }) =>
      Promise.resolve(promise)
        .then((stats) => {
          applyResult(vuex, stats, dataset)
          vuex.commit("setStreamingRequestState", { key, loading: false, error: null })
        })
        .catch((error) => {
          vuex.commit("setStreamingRequestState", {
            key,
            loading: false,
            error: error?.message || "Failed to fetch analytics.",
          })
          throw error
        })
    )
  )

  if (results.every((result) => result.status === "rejected")) {
    throw results[0].reason
  }
}

/**
 * Fetch primary and comparison requests in parallel, committing all results
 * together so both datasets appear on screen simultaneously.
 */
async function fetchBothWithRequestStates(vuex, primaryRequests, comparisonRequests) {
  primaryRequests.forEach(({ key }) => {
    vuex.commit("setStreamingRequestState", { key, loading: true, error: null })
  })

  // Both Promise.allSettled calls run concurrently — all API calls are in flight
  const [primaryResults, comparisonResults] = await Promise.all([
    Promise.allSettled(
      primaryRequests.map(({ key, promise }) =>
        Promise.resolve(promise).then((stats) => ({ key, stats })),
      ),
    ),
    Promise.allSettled(
      comparisonRequests.map(({ key, promise }) =>
        Promise.resolve(promise).then((stats) => ({ key, stats })),
      ),
    ),
  ])

  // Commit all primary results
  primaryResults.forEach((result, i) => {
    const { key } = primaryRequests[i]
    if (result.status === "fulfilled") {
      applyResult(vuex, result.value.stats, "primary")
      vuex.commit("setStreamingRequestState", { key, loading: false, error: null })
    } else {
      vuex.commit("setStreamingRequestState", {
        key,
        loading: false,
        error: result.reason?.message || "Failed to fetch analytics.",
      })
    }
  })

  // Commit all comparison results (error-isolated)
  if (comparisonResults.every((r) => r.status === "rejected")) {
    console.warn("Comparison fetch failed, primary data preserved")
    vuex.commit("setComparisonMode", false)
  } else {
    comparisonResults.forEach((result) => {
      if (result.status === "fulfilled") {
        applyResult(vuex, result.value.stats, "comparison")
      }
    })
  }

  // If all primary requests failed, throw
  if (primaryResults.every((r) => r.status === "rejected")) {
    throw primaryResults[0].reason
  }
}

// ---------------------------------------------------------------------------
// Unified analytics action
// ---------------------------------------------------------------------------

/**
 * Single entry point for all analytics fetching.
 *
 * When comparison is enabled, primary and comparison datasets are fetched in
 * parallel and committed together so the UI renders both simultaneously.
 *
 * @param {Object} vuex      Vuex action context
 * @param {Object} strategy  A strategy object from `./strategies.js`
 * @param {Object} params    Params from the filters UI
 */
export const fetchAnalytics = async (vuex, { strategy, params }) => {
  vuex.commit("reset")
  vuex.commit("setParams", params)

  const isDayPeriod = params.from_date === params.to_date
  if (isDayPeriod) {
    vuex.commit("setPeriodType", "day")
  }

  const accountKey = vuex.rootGetters["Account/account"]?.key

  const wantsComparison =
    params.enableComparison &&
    params.comparisonFromDate &&
    params.comparisonToDate &&
    strategy.buildComparisonParams

  if (wantsComparison) {
    // --- Synchronized parallel fetch (primary + comparison) ---
    const comparisonParams = strategy.buildComparisonParams(params)
    vuex.commit("setComparisonParams", comparisonParams)

    const isCompDay = comparisonParams.from_date === comparisonParams.to_date
    vuex.commit("setComparisonPeriodType", isCompDay ? "day" : "period")
    vuex.commit("setComparisonMode", true)

    if (strategy.useRequestStates) {
      vuex.commit("setIsStreamingAnalyticsRequested", true)

      // Both buildRequests calls fire API promises immediately
      const primaryRequests = strategy.buildRequests(params, isDayPeriod, accountKey)
      const comparisonRequests = strategy.buildRequests(comparisonParams, isCompDay, accountKey)

      await fetchBothWithRequestStates(vuex, primaryRequests, comparisonRequests)
    } else {
      // Non-requestStates strategies: parallel fetchData + batch commit
      const [primaryResult, comparisonResult] = await Promise.allSettled([
        strategy.fetchData(params, isDayPeriod, accountKey),
        strategy.fetchData(comparisonParams, isCompDay, accountKey),
      ])

      if (primaryResult.status === "fulfilled") {
        vuex.commit("setDatasetStat", { dataset: "primary", ...primaryResult.value })
      } else {
        throw primaryResult.reason
      }

      if (comparisonResult.status === "fulfilled") {
        vuex.commit("setDatasetStat", { dataset: "comparison", ...comparisonResult.value })
      } else {
        console.warn("Comparison fetch failed, primary data preserved:", comparisonResult.reason)
        vuex.commit("setComparisonMode", false)
      }
    }
  } else {
    // --- Primary-only (progressive loading) ---
    vuex.commit("setComparisonMode", false)

    if (strategy.useRequestStates) {
      vuex.commit("setIsStreamingAnalyticsRequested", true)
      const requests = strategy.buildRequests(params, isDayPeriod, accountKey)
      await fetchWithRequestStates(vuex, requests, "primary")
    } else {
      const result = await strategy.fetchData(params, isDayPeriod, accountKey)
      vuex.commit("setDatasetStat", { dataset: "primary", ...result })
    }
  }
}

// ---------------------------------------------------------------------------
// City analytics
// ---------------------------------------------------------------------------

export const fetchAccountCityAnalytics = async (vuex, { accountId, countryCode, includeOutputs }) => {
  const cityAnalytics = await getAccountCityStats(accountId, countryCode, includeOutputs)
  vuex.commit("setCityStats", cityAnalytics)
}

export const fetchStationCityAnalytics = async (vuex, { mountPoint, countryCode, includeOutputs }) => {
  const cityAnalytics = await getStationCityStats(mountPoint, countryCode, includeOutputs)
  vuex.commit("setCityStats", cityAnalytics)
}

// ---------------------------------------------------------------------------
// Reset
// ---------------------------------------------------------------------------

export const resetAnalytics = async (context) => {
  context.commit("reset")
}
